import { useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../lib/util/money';
import * as types from '../types';
import useSubscriptionProducts from './useSubscriptionProducts';

/**
 * A GwP code takes off the 6-month prepaid plan's price, so that price, in the shopper's currency, is the discount
 * to advertise. Null when there's no 6-month plan or it isn't priced in that currency, so callers hide the line
 * rather than show a wrong amount.
 */
export function gwpDiscountAmount(
  plans: Pick<types.ISubscriptionProduct, 'renewalMonths' | 'pricingInCents'>[],
  currency: types.FiCurrency,
  formatter: Intl.NumberFormat,
) {
  const sixMonthPlan = plans.find((p) => p.renewalMonths === 6);
  if (!sixMonthPlan) return null;
  try {
    return formatter.format(centsToDollarsNumber(getLocalizedPricing(sixMonthPlan.pricingInCents, currency)));
  } catch {
    // getLocalizedPricing throws if the locale's currency isn't on the plan.
    return null;
  }
}

/** The GwP discount, formatted in the shopper's currency. Mini and Series 3+ share it, so Series 3+ plans are read. */
export default function useGwpDiscountAmount() {
  const { currency, wholeNumberCurrencyFormatter } = useLocale();
  const series3PlusSubscriptions = useSubscriptionProducts(types.series3PlusCollarId);
  return useMemo(
    () => gwpDiscountAmount(series3PlusSubscriptions, currency, wholeNumberCurrencyFormatter),
    [series3PlusSubscriptions, currency, wholeNumberCurrencyFormatter],
  );
}
